import React from 'react';
import { Button, Modal } from '@nsi-advisory-services/components';
import { UI, Section, Note, List, Chapter } from '../shared/helpModalParts';

// A walkthrough of filing a time off request, written against the actual
// controls: every named thing below is a field, button or badge that exists on
// the page, so the reader can match what they are told to what they see.
//
// `canScheduleForOthers` mirrors the page's own rule for who may file on
// someone else's behalf, and `isEditMode` swaps the wording for the case where
// an existing pending request is being changed rather than created.
export default function TimeOffCreateHelpModal({
  open,
  onClose,
  canScheduleForOthers = false,
  isEditMode = false,
}) {
  const chapters = [
    {
      key: 'dates',
      label: 'Step 1 — pick the days',
      icon: 'bi-calendar-range',
      body: (
        <>
          <Section title="">
            Click a day on the calendar to set the start, then click another to
            set the end and the range fills in between them, marked{' '}
            <UI>START</UI> and <UI>END</UI>. Or type the dates into{' '}
            <UI>Start Date</UI> and <UI>End Date</UI> underneath. Both drive the
            same selection, so use whichever is easier for you.
            <List>
              <li>One day off? Click the same day twice, or set both dates to it.</li>
              <li>
                Changed your mind? <UI icon="bi-x-lg">Clear</UI> at the top of
                the calendar empties the selection.
              </li>
            </List>
          </Section>

          <Section title="What the colors mean">
            The legend across the top of the calendar names them:
            <List>
              <li><strong>Close Date</strong> the company is already closed — no request needed.</li>
              <li><strong>Existing Request</strong> time off you have already filed.</li>
              <li><strong>Approved</strong> and <strong>Pending</strong> where those requests stand.</li>
            </List>
            <UI>Days selected</UI> under the date fields counts what you have
            picked, weekends and holidays included. It is the span, not the
            hours.
          </Section>

          <Section title="Moving on">
            <UI>Next</UI> is disabled until a start and an end are set. If the
            days you picked run into a company closure or one of your existing
            requests, you will be told before the form continues:
            <List>
              <li>
                <strong>Close Date Already Covers Selection</strong> the company
                is closed then, so you do not need to book the time.
              </li>
              <li>
                <strong>Overlaps with</strong> lists the requests already on
                those days, so you can go back rather than file twice.
              </li>
            </List>
          </Section>
        </>
      ),
    },
    {
      key: 'details',
      label: 'Step 2 — the details',
      icon: 'bi-pencil-square',
      body: (
        <>
          <Section title="Request Type">
            Always <UI>Time off</UI>. For legal reasons tracked time off is not
            split into sick and vacation, so there is nothing to choose here.
          </Section>

          <Section title="Calculated Working Hours">
            Filled in from the days you picked and is working days only, so weekends
            and company close dates are already left out. The line underneath
            shows what was calculated. Overwrite it if your case is different,
            for example a half day.
          </Section>

          <Section title="Needs review?">
            Leave it unticked for ordinary time off. Tick it only when you are
            expecting to be <strong>reimbursed</strong> for the time. This is
            what sends the request to an approver instead of simply recording
            it.
          </Section>

          <Section title="Add to company calendar?">
            Puts the days on the shared calendar so the rest of the company can
            see you are out. It has no effect on approval.
          </Section>

          <Section title="Comment">
            Required. A line on what the time is for. It is what an approver
            reads first, and what you will see on the request later.
          </Section>

          <Note title="Nothing is filed until you finish">
            <UI>Back</UI> returns to the calendar with your selection intact.
            The request only exists once you press the button at the bottom
            right.
          </Note>
        </>
      ),
    },
    {
      key: 'after',
      label: 'After you submit',
      icon: 'bi-send-check',
      body: (
        <>
          <Section title="Where it goes">
            A request that needs review lands under <UI>Pending</UI> on the
            My Time Off page until someone decides it. One that does not
            is recorded straight away and shows under <UI>Entered</UI>.
          </Section>

          <Section title="Changing your mind later">
            Open the request from that page. While it is still pending you can
            modify or withdraw it; modifying cancels the pending approval and
            files the corrected version in its place.
          </Section>
        </>
      ),
    },
  ];

  if (isEditMode) {
    chapters.push({
      key: 'editing',
      label: 'You are modifying a request',
      icon: 'bi-arrow-repeat',
      body: (
        <>
          <Section title="What submitting does">
            This form is loaded with an existing pending request.{' '}
            <UI>Submit Modifications</UI> cancels that pending approval and
            creates a new request from what is on screen, so it starts its
            review afresh.
          </Section>

          <Section title="Backing out">
            <UI>Cancel modification</UI> in the bar at the bottom leaves the
            original request exactly as it was.
          </Section>
        </>
      ),
    });
  }

  if (canScheduleForOthers) {
    chapters.push({
      key: 'others',
      label: 'Filing for someone else',
      icon: 'bi-people',
      body: (
        <>
          <Section title="Pick the person first">
            <UI>Requesting For</UI> at the top right lists the people you may
            file for. Choose before you pick dates: the calendar redraws with
            that person's existing requests, and the hours are calculated
            against their schedule.
          </Section>

          <Note title="The request is theirs">
            Whoever is selected owns the request. It appears on their record
            and against their balance, not yours, regardless of who filled the form in.
          </Note>
        </>
      ),
    });
  }

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <Modal.Header>
        <Modal.Title>
          <i className="bi bi-question-circle me-2" aria-hidden="true" />
          How to request time off
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {chapters.map((chapter, i) => (
          <Chapter
            key={chapter.key}
            title={chapter.label}
            icon={chapter.icon}
            first={i === 0}
          >
            {chapter.body}
          </Chapter>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
